import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiJson, ApiError } from '@/lib/api';
import type { AdminUserDetailResponse, SendDripEmailResponse } from '@/lib/types';

type PushResult = { success: boolean; users_notified: number; message: string };
type SmsResult = { success: boolean; message: string };
type ExportResult = { success: boolean; message: string };
type OverrideResult = { user_id: number; subscription_override: boolean };

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string };
      if (j.detail) return j.detail;
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

function AdminUserActionsPage() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const detail = userQuery.data;

  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushDeeplink, setPushDeeplink] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [smsConfirm, setSmsConfirm] = useState(false);
  const [dripCampaign, setDripCampaign] = useState('');
  const [dripEmailKey, setDripEmailKey] = useState('');
  const [exportIncludeMessages, setExportIncludeMessages] = useState(true);
  const [exportIncludeAccounting, setExportIncludeAccounting] = useState(true);
  const [exportIncludeCalendar, setExportIncludeCalendar] = useState(true);
  const [exportIncludeCheckIns, setExportIncludeCheckIns] = useState(true);
  const [exportIncludeExchange, setExportIncludeExchange] = useState(true);
  const [exportIncludeJournal, setExportIncludeJournal] = useState(true);

  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const pushMutation = useMutation({
    mutationFn: () =>
      apiJson<PushResult>(getToken, `/v1/admin/users/${id}/notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: pushTitle,
          body: pushBody,
          deeplink: pushDeeplink.trim() || null,
        }),
      }),
    onSuccess: (data) => {
      setBanner({ ok: data.success, text: data.message });
      if (data.success) {
        setPushTitle('');
        setPushBody('');
        setPushDeeplink('');
      }
    },
    onError: (e) => setBanner({ ok: false, text: parseDetail(e) }),
  });

  const smsMutation = useMutation({
    mutationFn: () => {
      const to = detail?.phone_number;
      if (!to) throw new Error('User has no phone number');
      return apiJson<SmsResult>(getToken, '/v1/admin/send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, message: smsBody }),
      });
    },
    onSuccess: (data) => {
      setBanner({ ok: data.success, text: data.message });
      if (data.success) setSmsBody('');
    },
    onError: (e) => setBanner({ ok: false, text: parseDetail(e) }),
  });

  const overrideMutation = useMutation({
    mutationFn: (override: boolean) =>
      apiJson<OverrideResult>(getToken, `/v1/admin/users/${id}/subscription-override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ override }),
      }),
    onSuccess: async () => {
      setBanner({ ok: true, text: 'Subscription override updated.' });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'user-detail', id] });
    },
    onError: (e) => setBanner({ ok: false, text: parseDetail(e) }),
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      apiJson<ExportResult>(getToken, '/v1/admin/export-user-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: id,
          include_messages: exportIncludeMessages,
          include_accounting: exportIncludeAccounting,
          include_calendar: exportIncludeCalendar,
          include_check_ins: exportIncludeCheckIns,
          include_exchange_compliance: exportIncludeExchange,
          include_journal: exportIncludeJournal,
        }),
      }),
    onSuccess: (data) => setBanner({ ok: data.success, text: data.message }),
    onError: (e) => setBanner({ ok: false, text: parseDetail(e) }),
  });

  const dripMutation = useMutation({
    mutationFn: () =>
      apiJson<SendDripEmailResponse>(getToken, '/v1/admin/send-drip-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_ids: [id],
          campaign_name: dripCampaign,
          email_key: dripEmailKey,
        }),
      }),
    onSuccess: (data) => setBanner({ ok: data.success, text: data.message }),
    onError: (e) => setBanner({ ok: false, text: parseDetail(e) }),
  });

  if (!detail) {
    return (
      <div className='flex justify-center py-12'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <h2 className='text-lg font-semibold text-gray-900'>Actions</h2>

      {banner ? (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            banner.ok
              ? 'bg-green-50 border-green-200 text-green-900'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {banner.text}
        </div>
      ) : null}

      <div className='bg-white rounded-lg border border-gray-200 p-4 space-y-3'>
        <h3 className='font-semibold text-gray-900'>Send push</h3>
        {!detail.has_push_token ? (
          <p className='text-sm text-gray-500'>User has no push token registered.</p>
        ) : (
          <>
            <input
              value={pushTitle}
              onChange={(e) => setPushTitle(e.target.value)}
              placeholder='Title'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
            <textarea
              value={pushBody}
              onChange={(e) => setPushBody(e.target.value)}
              placeholder='Body'
              rows={3}
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
            <input
              value={pushDeeplink}
              onChange={(e) => setPushDeeplink(e.target.value)}
              placeholder='Deeplink (optional)'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
            <button
              type='button'
              disabled={pushMutation.isPending || !pushTitle.trim() || !pushBody.trim()}
              onClick={() => pushMutation.mutate()}
              className='px-4 py-2 text-sm font-semibold bg-primary text-white rounded-md disabled:opacity-50'
            >
              {pushMutation.isPending ? 'Sending…' : 'Send push'}
            </button>
          </>
        )}
      </div>

      <div className='bg-white rounded-lg border border-gray-200 p-4 space-y-3'>
        <h3 className='font-semibold text-gray-900'>Send SMS</h3>
        <p className='text-xs text-gray-500'>
          To: <strong>{detail.phone_number ?? '—'}</strong>
        </p>
        <textarea
          value={smsBody}
          onChange={(e) => setSmsBody(e.target.value)}
          placeholder='SMS message'
          rows={3}
          className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
        />
        <label className='flex items-center gap-2 text-sm text-gray-700'>
          <input
            type='checkbox'
            checked={smsConfirm}
            onChange={(e) => setSmsConfirm(e.target.checked)}
          />
          I confirm send to {detail.phone_number ?? '(no phone)'}
        </label>
        <button
          type='button'
          disabled={
            smsMutation.isPending || !detail.phone_number || !smsBody.trim() || !smsConfirm
          }
          onClick={() => smsMutation.mutate()}
          className='px-4 py-2 text-sm font-semibold border border-gray-300 rounded-md disabled:opacity-50'
        >
          {smsMutation.isPending ? 'Sending…' : 'Send SMS'}
        </button>
      </div>

      <div className='bg-white rounded-lg border border-gray-200 p-4 space-y-3'>
        <h3 className='font-semibold text-gray-900'>Subscription override</h3>
        <p className='text-sm text-gray-600'>
          Current:{' '}
          <strong>{detail.subscription_override ? 'On (override)' : 'Off'}</strong>
        </p>
        <button
          type='button'
          disabled={overrideMutation.isPending}
          onClick={() => overrideMutation.mutate(!detail.subscription_override)}
          className='px-4 py-2 text-sm font-semibold border border-gray-300 rounded-md disabled:opacity-50'
        >
          {overrideMutation.isPending
            ? 'Saving…'
            : detail.subscription_override
              ? 'Remove override'
              : 'Grant override'}
        </button>
      </div>

      <div className='bg-white rounded-lg border border-gray-200 p-4 space-y-3'>
        <h3 className='font-semibold text-gray-900'>Export user data (admin verification email)</h3>
        <div className='grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm'>
          {(
            [
              ['Messages', exportIncludeMessages, setExportIncludeMessages],
              ['Accounting', exportIncludeAccounting, setExportIncludeAccounting],
              ['Calendar', exportIncludeCalendar, setExportIncludeCalendar],
              ['Check-ins', exportIncludeCheckIns, setExportIncludeCheckIns],
              ['Exchange compliance', exportIncludeExchange, setExportIncludeExchange],
              ['Journal', exportIncludeJournal, setExportIncludeJournal],
            ] as const
          ).map(([label, checked, set]) => (
            <label key={label} className='flex items-center gap-2'>
              <input type='checkbox' checked={checked} onChange={(e) => set(e.target.checked)} />
              {label}
            </label>
          ))}
        </div>
        <button
          type='button'
          disabled={exportMutation.isPending}
          onClick={() => exportMutation.mutate()}
          className='px-4 py-2 text-sm font-semibold bg-gray-900 text-white rounded-md disabled:opacity-50'
        >
          {exportMutation.isPending ? 'Starting…' : 'Trigger export'}
        </button>
      </div>

      <div className='bg-white rounded-lg border border-gray-200 p-4 space-y-3'>
        <h3 className='font-semibold text-gray-900'>Send drip email</h3>
        <input
          value={dripCampaign}
          onChange={(e) => setDripCampaign(e.target.value)}
          placeholder='campaign_name (e.g. activation)'
          className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
        />
        <input
          value={dripEmailKey}
          onChange={(e) => setDripEmailKey(e.target.value)}
          placeholder='email_key (e.g. activation_day_1)'
          className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
        />
        <button
          type='button'
          disabled={dripMutation.isPending || !dripCampaign.trim() || !dripEmailKey.trim()}
          onClick={() => dripMutation.mutate()}
          className='px-4 py-2 text-sm font-semibold border border-gray-300 rounded-md disabled:opacity-50'
        >
          {dripMutation.isPending ? 'Sending…' : 'Send drip email'}
        </button>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId/actions')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserActionsPage,
});
